'use client';

import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { Icon, type IconName } from '../../../components/icons';
import { NavLink } from '../../../components/ui/NavLink';
import { AppUserRole } from '../../../constants/appUserRole';
import {
  ROUTES,
  adoptShelterQuizPathForShelter,
} from '../../../constants/routes';
import { Button } from '../../../components/ui/Button';
import { IconButton } from '../../../components/ui/icon-button';
import { buttonVariants } from '../../../components/ui/button-variants';
import { cn } from '../../../lib/utils';
import {
  isShelterAdministrator,
  isShelterMember,
  isSuperAdmin,
} from '../../../lib/rbac';
import { useSignOut } from '../../auth/hooks/useSignOut';
import {
  useAuthSession,
  isAuthenticatedSession,
} from '../../auth/hooks/useAuthSession';
import {
  getShelterById,
  getShelterLogoViewUrl,
  type Shelter,
} from '../../shelters/api/shelters-api';
import { shelterKeys } from '../../shelters/queryKeys';
import { themeConfig } from '../../../theme';
import { BrandLogo } from './brand-logo';
import { SidebarNavItem } from './sidebar-nav-item';

/** Preserves existing users' collapse preference; key not renamed intentionally. */
const STORAGE_KEY = 'friends-for-life-admin-sidebar-collapsed';

function sidebarIcon(name: IconName) {
  return <Icon name={name} className='size-5' />;
}

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

type NavEntry = {
  to: string;
  label: string;
  end?: boolean;
  icon: ReactNode;
  openInNewTab?: boolean;
};

export function AppSidebar() {
  const signOut = useSignOut();
  const { data: session } = useAuthSession();

  const isAuthenticated = isAuthenticatedSession(session);
  const u = isAuthenticated ? session.data.user : null;
  const superAdmin = isAuthenticated && isSuperAdmin(u!.role);
  const shelterAdmin =
    isAuthenticated && isShelterAdministrator(u!.role, u!.shelterRole);
  /** Roster `user` in a shelter (not a shelter-level admin). */
  const shelterMemberOnly =
    isAuthenticated &&
    isShelterMember(u!.shelterId) &&
    !isSuperAdmin(u!.role) &&
    !isShelterAdministrator(u!.role, u!.shelterRole);

  const shelterId = u?.shelterId;

  const shelterDetailQuery = useQuery<Shelter>({
    queryKey: shelterId
      ? shelterKeys.detail(shelterId)
      : ['shelters', 'detail', 'none'],
    queryFn: () => getShelterById(shelterId!),
    enabled: Boolean(isAuthenticated && shelterId),
  });

  const shelterLogoS3Key = useMemo(() => {
    const s = shelterDetailQuery.data?.settings as
      | Record<string, unknown>
      | undefined;
    const logo = s?.shelterLogo;
    if (!logo || typeof logo !== 'object' || Array.isArray(logo)) {
      return undefined;
    }
    const key = (logo as Record<string, unknown>).s3Key;
    return typeof key === 'string' && key.trim() ? key.trim() : undefined;
  }, [shelterDetailQuery.data?.settings]);

  const shelterLogoViewQuery = useQuery({
    queryKey: shelterId
      ? shelterKeys.logoViewUrl(shelterId)
      : [...shelterKeys.all, 'logoViewUrl', 'none'],
    queryFn: () => getShelterLogoViewUrl(shelterId!),
    enabled: Boolean(isAuthenticated && shelterId && shelterLogoS3Key),
    staleTime: 60_000,
  });

  const shelterBrandImageSrc =
    shelterLogoS3Key && shelterLogoViewQuery.data?.signedUrl
      ? shelterLogoViewQuery.data.signedUrl
      : undefined;

  const sidebarWordmark = useMemo(() => {
    const fallback = themeConfig.brand.shortName;
    if (!isAuthenticated || !u) return fallback;
    if (isSuperAdmin(u.role)) return 'Admin Console';
    if (u.role === AppUserRole.SHELTER_ADMIN || u.role === AppUserRole.USER) {
      if (!u.shelterId) return fallback;
      const detail = shelterDetailQuery.data as Shelter | undefined;
      if (shelterDetailQuery.isLoading && !detail?.name) {
        return 'Loading…';
      }
      const name = detail?.name?.trim();
      return name && name.length > 0 ? name : fallback;
    }
    return fallback;
  }, [
    isAuthenticated,
    u,
    shelterDetailQuery.data,
    shelterDetailQuery.isLoading,
  ]);

  const shelterPublicQuizHref = useMemo(() => {
    if (!shelterId) return ROUTES.home;
    return adoptShelterQuizPathForShelter(
      shelterId,
      shelterDetailQuery.data?.settings,
    );
  }, [shelterId, shelterDetailQuery.data?.settings]);

  const publicSiteHref =
    isAuthenticated && Boolean(shelterId) && !superAdmin
      ? shelterPublicQuizHref
      : ROUTES.home;

  const items: NavEntry[] = [
    {
      to: ROUTES.admin,
      label: 'Dashboard',
      end: true,
      icon: sidebarIcon('dashboard-grid-outline'),
    },
    ...(superAdmin
      ? [
          {
            to: ROUTES.adminSubscriptions,
            label: 'Subscriptions',
            icon: sidebarIcon('credit-card-outline'),
          },
          {
            to: ROUTES.adminUsers,
            label: 'Tenants',
            icon: sidebarIcon('users-pair-outline'),
          },
          {
            to: ROUTES.adminAnalytics,
            label: 'Analytics',
            icon: sidebarIcon('analytics'),
          },
          {
            to: ROUTES.adminShelters,
            label: 'Shelters',
            icon: sidebarIcon('building-outline'),
          },
          {
            to: ROUTES.adminMatchQuestions,
            label: 'Match quiz',
            icon: sidebarIcon('file-text-outline'),
          },
        ]
      : []),
    ...(shelterAdmin || shelterMemberOnly
      ? [
          {
            to: ROUTES.adminPets,
            label: 'Pets',
            icon: sidebarIcon('paw-print-outline'),
          },
          {
            to: ROUTES.adminShelterAnalytics,
            label: 'Analytics',
            icon: sidebarIcon('analytics'),
          },
        ]
      : []),
    ...(shelterAdmin
      ? [
          {
            to: ROUTES.adminShelterTraining,
            label: 'Training',
            icon: sidebarIcon('bone-outline'),
          },
          {
            to: ROUTES.adminShelterUsers,
            label: 'Users',
            icon: sidebarIcon('users-pair-outline'),
          },
          {
            to: ROUTES.adminShelterSubscription,
            label: 'Subscription',
            icon: sidebarIcon('credit-card-outline'),
          },
        ]
      : []),
    {
      to: publicSiteHref,
      label: 'Public site',
      icon: sidebarIcon('external-link-outline'),
      openInNewTab: true,
    },
  ];

  const [collapsed, setCollapsed] = useState(readCollapsed);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0');
    } catch {
      // ignore
    }
  }, [collapsed]);

  const toggle = useCallback(() => {
    setCollapsed((c) => !c);
  }, []);

  const settingsFooterClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      buttonVariants({ variant: 'outline', size: 'icon' }),
      'shrink-0',
      isActive && 'border-primary-600 bg-primary-50 text-primary-800',
    );

  return (
    <aside
      className={cn(
        'sticky top-3 flex h-[calc(100dvh-1.5rem)] max-h-[calc(100dvh-1.5rem)] shrink-0 flex-col rounded-2xl bg-muted transition-[width] duration-200 ease-out sm:top-4 sm:h-[calc(100dvh-2rem)] sm:max-h-[calc(100dvh-2rem)] sm:rounded-3xl',
        collapsed ? 'w-17' : 'w-56 sm:w-60 lg:w-64',
      )}
      aria-label='Workspace navigation'
    >
      <div className='flex min-h-14 shrink-0 items-center gap-2 px-2.5 py-3 sm:gap-2.5 sm:px-3 sm:py-3.5'>
        <IconButton
          type='button'
          variant='ghost'
          onClick={toggle}
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className='shrink-0 text-muted-foreground hover:bg-accent/60 hover:text-foreground'
        >
          {collapsed
            ? sidebarIcon('door-enter-outline')
            : sidebarIcon('door-exit-outline')}
        </IconButton>
        <BrandLogo
          to={ROUTES.admin}
          compact={collapsed}
          layout={shelterBrandImageSrc ? 'icon' : 'horizontal'}
          className='min-w-0 flex-1 justify-start'
          customBrandImageSrc={shelterBrandImageSrc}
          horizontalLogoSrc={themeConfig.logos.brandingHorizontal}
          wordmark={
            superAdmin && !shelterBrandImageSrc ? undefined : sidebarWordmark
          }
        />
      </div>

      <nav
        id='admin-sidebar-primary'
        className='flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-2.5 pb-2 pt-1 sm:gap-2.5 sm:px-3'
        aria-label='Primary'
      >
        {items.map(({ to, label, end, icon, openInNewTab }) => (
          <SidebarNavItem
            key={`${label}-${to}`}
            to={to}
            end={end}
            label={label}
            icon={icon}
            collapsed={collapsed}
            openInNewTab={openInNewTab}
          />
        ))}
      </nav>

      <div className='mt-auto flex shrink-0 flex-col px-2.5 pb-3 pt-2 sm:px-3 sm:pb-3.5'>
        {collapsed ? (
          <IconButton
            type='button'
            variant='outline'
            onClick={signOut}
            aria-label='Sign out'
            className='w-full'
          >
            {sidebarIcon('log-out-outline')}
          </IconButton>
        ) : (
          <div className='flex items-center gap-2 sm:gap-2.5'>
            <Button
              type='button'
              variant='outline'
              className='min-w-0 flex-1 justify-center gap-2 font-medium'
              onClick={signOut}
            >
              {sidebarIcon('log-out-outline')}
              Sign out
            </Button>
            <NavLink
              href={ROUTES.adminSettings}
              className={settingsFooterClass}
              aria-label='Settings'
              title='Settings'
            >
              {sidebarIcon('settings-gear-outline')}
            </NavLink>
          </div>
        )}
      </div>
    </aside>
  );
}
